import { useState, useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import { Loader2, ImageOff, RefreshCw } from 'lucide-react'

export function ReportImage({ src, alt, className }: { src: string; alt: string; className?: string }) {
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading')
  const [retrySrc, setRetrySrc] = useState(src)
  const retryCount = useRef(0)

  useEffect(() => { setRetrySrc(src); setStatus('loading'); retryCount.current = 0 }, [src])

  const handleError = () => {
    if (retryCount.current < 2) {
      retryCount.current++
      const sep = src.includes('?') ? '&' : '?'
      setRetrySrc(`${src}${sep}_retry=${retryCount.current}&t=${Date.now()}`)
    } else {
      setStatus('error')
    }
  }

  if (status === 'error') {
    return (
      <div className={cn('flex flex-col items-center justify-center bg-slate-100 text-slate-400', className)}>
        <ImageOff className="w-6 h-6 mb-1" />
        <span className="text-[10px]">Image unavailable</span>
        <button
          onClick={() => { retryCount.current = 0; setRetrySrc(`${src}${src.includes('?') ? '&' : '?'}t=${Date.now()}`); setStatus('loading') }}
          className="mt-1 text-[10px] text-blue-500 hover:underline flex items-center gap-0.5"
        >
          <RefreshCw className="w-2.5 h-2.5" /> Retry
        </button>
      </div>
    )
  }

  return (
    <div className={cn('relative', className)}>
      {status === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-100">
          <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
        </div>
      )}
      <img
        src={retrySrc}
        alt={alt}
        className="w-full h-full object-cover"
        loading="lazy"
        onLoad={() => setStatus('loaded')}
        onError={handleError}
      />
    </div>
  )
}
